import { useMemo, useState } from "react"

import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Drawer,
  IconButton,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material"

import MenuRoundedIcon from "@mui/icons-material/MenuRounded"
import CloseRoundedIcon from "@mui/icons-material/CloseRounded"
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded"
import PointOfSaleRoundedIcon from "@mui/icons-material/PointOfSaleRounded"
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded"
import LocalShippingRoundedIcon from "@mui/icons-material/LocalShippingRounded"
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded"
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded"
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded"
import SecurityRoundedIcon from "@mui/icons-material/SecurityRounded"
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded"
import WarehouseRoundedIcon from "@mui/icons-material/WarehouseRounded"
import PhoneIphoneRoundedIcon from "@mui/icons-material/PhoneIphoneRounded"
import EmailRoundedIcon from "@mui/icons-material/EmailRounded"
import WhatsAppIcon from "@mui/icons-material/WhatsApp"
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded"
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded"
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded"
import PersonRoundedIcon from "@mui/icons-material/PersonRounded"
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded"

import { Link as RouterLink } from "react-router-dom"

// =========================================================
// CONTACT / ASSETS
// =========================================================

const WHATSAPP_NUMBER = "243810289991"
const WHATSAPP_DISPLAY = "+243 810 289 991"
const EMAIL = "rlsys.business@gmail.com"
const BROCHURE_URL = "/RLSys_Business_Brochure.pdf"

const whatsappMessage =
  "Bonjour RLSys Business, je souhaite demander une démonstration de votre plateforme ERP All-in-One."

const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
  whatsappMessage
)}`

// =========================================================
// CONTENT
// =========================================================

const modules = [
  {
    icon: <Inventory2RoundedIcon />,
    title: "Fournisseurs & achats",
    text:
      "Demandes d’achat, fournisseurs, bons de commande, réceptions, factures et règlements fournisseurs.",
  },
  {
    icon: <RocketLaunchRoundedIcon />,
    title: "Production",
    text:
      "Nomenclatures, ordres de production, consommations, produits finis et suivi des opérations de fabrication.",
  },
  {
    icon: <WarehouseRoundedIcon />,
    title: "Entrepôts",
    text:
      "Multi-entrepôts, entrées, sorties, transferts, affectations, valorisation et disponibilité par emplacement.",
  },
  {
    icon: <Inventory2RoundedIcon />,
    title: "Stock & inventaire",
    text:
      "Stock réel, réservations, alertes, mouvements, inventaires, audits et contrôle des écarts.",
  },
  {
    icon: <PointOfSaleRoundedIcon />,
    title: "Ventes & POS",
    text:
      "Ventes rapides, tickets, encaissements, historique, prix, remises et opérations de caisse au point de vente.",
  },
  {
    icon: <StorefrontRoundedIcon />,
    title: "Ventes terrain & agents",
    text:
      "Commerciaux terrain, clients, commandes, shops, dépôts relais, suivi des ventes et opérations mobiles.",
  },
  {
    icon: <PaymentsRoundedIcon />,
    title: "Paiements & caisse",
    text:
      "Paiements clients, dépôts caisse centrale, dépenses, rapprochements, écarts et suivi des encaissements.",
  },
  {
    icon: <PaymentsRoundedIcon />,
    title: "Finance, dettes & règlements",
    text:
      "Dettes clients et fournisseurs, règlements, suivi financier, marges et consolidation des flux monétaires.",
  },
  {
    icon: <LocalShippingRoundedIcon />,
    title: "Logistique & livraisons",
    text:
      "Tournées, véhicules, chauffeurs, livraisons, carburant, distances, retours et écarts terrain.",
  },
  {
    icon: <PeopleAltRoundedIcon />,
    title: "RH & équipes",
    text:
      "Employés, affectations, utilisateurs, responsabilités, présence opérationnelle et organisation des équipes.",
  },
  {
    icon: <DashboardRoundedIcon />,
    title: "Reporting & pilotage",
    text:
      "KPI, tableaux de bord, ventes, stock, production, finance, performance et vues de direction.",
  },
  {
    icon: <SecurityRoundedIcon />,
    title: "Administration & sécurité",
    text:
      "Rôles, permissions, organisations, traçabilité, journal d’activité et accès sécurisés par profil.",
  },
]

const processFlows = [
  {
    number: "01",
    title: "Approvisionner",
    text: "Fournisseurs → demandes → commandes → réceptions",
  },
  {
    number: "02",
    title: "Produire",
    text: "Nomenclatures → ordres → consommation → produits finis",
  },
  {
    number: "03",
    title: "Stocker & contrôler",
    text: "Entrepôts → mouvements → transferts → inventaires → alertes",
  },
  {
    number: "04",
    title: "Vendre partout",
    text: "Shops → POS → terrain → clients → paiements",
  },
  {
    number: "05",
    title: "Livrer",
    text: "Commandes → tournées → véhicules → chauffeurs → réception",
  },
  {
    number: "06",
    title: "Piloter",
    text: "Caisse → finance → RH → reporting → décisions",
  },
]

const sectors = [
  "Magasins & shops",
  "Supermarchés & supérettes",
  "Grossistes",
  "Distributeurs",
  "Dépôts & entrepôts",
  "Usines & production",
  "Industrie alimentaire",
  "Matelas & mousse",
  "Vivres & produits frais",
  "Quincailleries",
  "Matériaux de construction",
  "Pharmacies",
  "Réseaux multi-sites",
  "Commerciaux terrain",
  "Logistique & livraison",
  "Autres PME",
]

const benefits = [
  {
    icon: <RocketLaunchRoundedIcon />,
    title: "Automatisation de bout en bout",
    text:
      "Les opérations ne restent plus isolées : achats, production, stock, ventes, paiements et livraisons s’enchaînent dans des workflows cohérents.",
  },
  {
    icon: <DashboardRoundedIcon />,
    title: "Une seule source de vérité",
    text:
      "Les équipes travaillent sur les mêmes données, avec une vision consolidée des opérations et de la performance.",
  },
  {
    icon: <SecurityRoundedIcon />,
    title: "Contrôle & traçabilité",
    text:
      "Rôles, validations, historiques, audits et droits d’accès permettent de mieux sécuriser les processus de l’entreprise.",
  },
  {
    icon: <PhoneIphoneRoundedIcon />,
    title: "Bureau, terrain & POS",
    text:
      "Une expérience responsive pensée pour ordinateur, tablette, mobile et terminaux POS selon le contexte opérationnel.",
  },
]

const pricingPlans = [
  {
    title: "Essentiel",
    price: "Sur estimation",
    description:
      "Pour une petite structure qui souhaite centraliser les opérations essentielles et démarrer avec un périmètre maîtrisé.",
    features: [
      "Ventes & POS",
      "Stock, caisse et paiements",
      "Utilisateurs & rôles",
      "Tableaux de bord essentiels",
      "Configuration initiale",
      "Accompagnement au démarrage",
    ],
  },
  {
    title: "Croissance",
    price: "Sur estimation",
    description:
      "Pour une PME avec plusieurs utilisateurs, shops, dépôts ou entrepôts et des workflows plus structurés.",
    featured: true,
    features: [
      "Multi-sites et multi-entrepôts",
      "Achats & réapprovisionnement",
      "Dépenses, dettes et caisse",
      "Reporting avancé",
      "Workflows adaptés",
      "Accompagnement opérationnel",
    ],
  },
  {
    title: "Entreprise / Multi-sites",
    price: "Sur devis",
    description:
      "Pour les organisations combinant production, distribution, équipes terrain, logistique et pilotage de direction.",
    features: [
      "Production & achats",
      "Multi-shops / multi-entrepôts",
      "Finance et inventaires",
      "Logistique & équipes terrain",
      "Pilotage direction",
      "Configuration avancée",
    ],
  },
  {
    title: "Industriel / Sur mesure",
    price: "Sur devis",
    description:
      "Pour les organisations nécessitant des processus spécifiques, des intégrations externes ou des modules dédiés.",
    features: [
      "Analyse des processus",
      "Architecture dédiée",
      "Modules spécifiques",
      "Intégrations externes",
      "Déploiement personnalisé",
      "Accompagnement sur mesure",
    ],
  },
]

const sectionSx = {
  scrollMarginTop: {
    xs: 88,
    md: 96,
  },
}

// =========================================================
// SHARED UI
// =========================================================

function SectionLabel({ children, dark = false }) {
  return (
    <Chip
      label={children}
      sx={{
        height: "auto",
        minHeight: 32,
        borderRadius: 999,
        color: dark ? "#dbeafe" : "#1e40af",
        background: dark
          ? "rgba(255,255,255,0.08)"
          : "linear-gradient(135deg, #eaf2ff 0%, #eef2ff 100%)",
        border: dark
          ? "1px solid rgba(255,255,255,0.13)"
          : "1px solid rgba(37,99,235,0.10)",
        fontWeight: 900,
        "& .MuiChip-label": {
          px: 1.5,
          py: 0.55,
          whiteSpace: "normal",
          textAlign: "center",
          lineHeight: 1.25,
        },
      }}
    />
  )
}

function NavButton({ children, href, onClick }) {
  return (
    <Button
      component="a"
      href={href}
      onClick={onClick}
      sx={{
        minWidth: 0,
        minHeight: 42,
        px: 1.35,
        color: "#1e293b",
        borderRadius: 2.2,
        fontSize: "0.91rem",
        fontWeight: 850,
        letterSpacing: "-0.01em",
        textTransform: "none",
        whiteSpace: "nowrap",
        "&:hover": {
          color: "#1d4ed8",
          bgcolor: "#eff6ff",
        },
      }}
    >
      {children}
    </Button>
  )
}

function DownloadBrochureButton({
  variant = "outlined",
  size = "medium",
  fullWidth = false,
  light = false,
}) {
  return (
    <Button
      component="a"
      href={BROCHURE_URL}
      download
      target="_blank"
      rel="noreferrer"
      variant={variant}
      size={size}
      fullWidth={fullWidth}
      startIcon={<DownloadRoundedIcon />}
      sx={{
        minHeight: size === "large" ? 54 : 46,
        px: size === "large" ? 2.6 : 2.2,
        fontSize: size === "large" ? "0.95rem" : "0.9rem",
        borderRadius: size === "large" ? 3 : 999,
        fontWeight: 900,
        textTransform: "none",
        whiteSpace: "nowrap",
        ...(light
          ? {
              color: "#ffffff",
              borderColor: "rgba(255,255,255,0.62)",
              "&:hover": {
                borderColor: "#ffffff",
                bgcolor: "rgba(255,255,255,0.08)",
              },
            }
          : {}),
      }}
    >
      Télécharger la brochure
    </Button>
  )
}

function ModuleCard({ item }) {
  return (
    <Card
      elevation={0}
      sx={{
        position: "relative",
        height: "100%",
        overflow: "hidden",
        borderRadius: 3.5,
        bgcolor: "#ffffff",
        border: "1px solid rgba(148,163,184,0.22)",
        boxShadow: "0 12px 34px rgba(15,23,42,0.055)",
        transition: "transform .2s ease, box-shadow .2s ease, border-color .2s ease",
        "&:hover": {
          transform: "translateY(-4px)",
          borderColor: "rgba(37,99,235,0.24)",
          boxShadow: "0 22px 48px rgba(15,23,42,0.10)",
        },
      }}
    >
      <CardContent
        sx={{
          p: { xs: 2.1, md: 2.4 },
          "&:last-child": { pb: { xs: 2.1, md: 2.4 } },
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            mb: 1.5,
            borderRadius: 2.6,
            display: "grid",
            placeItems: "center",
            color: "#1d4ed8",
            bgcolor: "#eff6ff",
            border: "1px solid #dbeafe",
          }}
        >
          {item.icon}
        </Box>

        <Typography
          component="h3"
          sx={{
            color: "#0f172a",
            fontSize: "1.05rem",
            fontWeight: 950,
            lineHeight: 1.25,
          }}
        >
          {item.title}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            mt: 0.8,
            color: "#64748b",
            lineHeight: 1.7,
          }}
        >
          {item.text}
        </Typography>
      </CardContent>
    </Card>
  )
}

function ProcessRow({ item, compact = false }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: compact ? "34px minmax(0,1fr)" : "42px minmax(0,1fr) auto",
        gap: 1.1,
        alignItems: "center",
        p: compact ? 1 : 1.15,
        borderRadius: 2.4,
        bgcolor: "#ffffff",
        border: "1px solid #e2e8f0",
      }}
    >
      <Box
        sx={{
          width: compact ? 34 : 40,
          height: compact ? 34 : 40,
          display: "grid",
          placeItems: "center",
          borderRadius: 2,
          color: "#ffffff",
          background: "linear-gradient(135deg,#1d4ed8,#4f46e5)",
          fontSize: compact ? "0.72rem" : "0.76rem",
          fontWeight: 950,
        }}
      >
        {item.number}
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            color: "#0f172a",
            fontSize: compact ? "0.82rem" : "0.9rem",
            fontWeight: 900,
            lineHeight: 1.2,
          }}
        >
          {item.title}
        </Typography>
        <Typography
          sx={{
            mt: 0.25,
            color: "#64748b",
            fontSize: compact ? "0.68rem" : "0.72rem",
            lineHeight: 1.35,
          }}
        >
          {item.text}
        </Typography>
      </Box>

      {!compact ? (
        <Chip
          label="Connecté"
          size="small"
          sx={{
            color: "#166534",
            bgcolor: "#dcfce7",
            fontWeight: 900,
            fontSize: "0.68rem",
          }}
        />
      ) : null}
    </Box>
  )
}

function OperationsPreview() {
  return (
    <Paper
      elevation={0}
      sx={{
        position: "relative",
        overflow: "hidden",
        p: { xs: 1.1, sm: 1.4, md: 1.6 },
        borderRadius: { xs: 4, md: 5 },
        background: "linear-gradient(145deg,#07132f 0%,#112b70 55%,#1d4ed8 100%)",
        border: "1px solid rgba(255,255,255,0.14)",
        boxShadow: "0 32px 90px rgba(15,23,42,0.22)",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          width: 300,
          height: 300,
          top: -160,
          right: -130,
          borderRadius: "50%",
          bgcolor: "rgba(255,255,255,0.08)",
        }}
      />

      <Paper
        elevation={0}
        sx={{
          position: "relative",
          zIndex: 2,
          p: { xs: 1.5, sm: 2, md: 2.3 },
          borderRadius: { xs: 3.2, md: 4 },
          bgcolor: "rgba(255,255,255,0.985)",
          boxShadow: "0 22px 55px rgba(2,6,23,0.22)",
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={1}
        >
          <Stack direction="row" spacing={1.1} alignItems="center" sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 42,
                height: 42,
                flexShrink: 0,
                borderRadius: 2.4,
                display: "grid",
                placeItems: "center",
                color: "#ffffff",
                background: "linear-gradient(135deg,#2563eb,#4338ca)",
              }}
            >
              <DashboardRoundedIcon />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 950, color: "#0f172a", lineHeight: 1.15 }}>
                RLSys Operations Center
              </Typography>
              <Typography variant="caption" sx={{ color: "#64748b" }}>
                Vision consolidée des processus de l’entreprise
              </Typography>
            </Box>
          </Stack>

          <Chip
            label="Temps réel"
            size="small"
            sx={{
              flexShrink: 0,
              color: "#166534",
              bgcolor: "#dcfce7",
              fontWeight: 900,
            }}
          />
        </Stack>

        <Box
          sx={{
            mt: 1.8,
            display: "grid",
            gridTemplateColumns: "repeat(3,minmax(0,1fr))",
            gap: 0.9,
          }}
        >
          {[
            ["Processus", "12+"],
            ["Sites", "Multi"],
            ["Contrôle", "24/7"],
          ].map(([label, value]) => (
            <Box
              key={label}
              sx={{
                p: 1.1,
                borderRadius: 2.4,
                bgcolor: "#f8fafc",
                border: "1px solid #e2e8f0",
              }}
            >
              <Typography sx={{ color: "#64748b", fontSize: "0.68rem" }}>
                {label}
              </Typography>
              <Typography sx={{ mt: 0.2, color: "#0f172a", fontWeight: 950 }}>
                {value}
              </Typography>
            </Box>
          ))}
        </Box>

        <Stack spacing={0.75} sx={{ mt: 1.2 }}>
          {processFlows.slice(0, 5).map((item) => (
            <ProcessRow key={item.number} item={item} compact />
          ))}
        </Stack>

        <Box
          sx={{
            mt: 1.2,
            p: 1.2,
            display: "flex",
            flexWrap: "wrap",
            gap: 0.7,
            alignItems: "center",
            borderRadius: 2.5,
            color: "#dbeafe",
            background: "linear-gradient(135deg,#0f2457,#1d4ed8)",
          }}
        >
          <RocketLaunchRoundedIcon sx={{ fontSize: 19 }} />
          <Typography sx={{ fontSize: "0.76rem", fontWeight: 900 }}>
            Automatisation All-in-One : une action peut déclencher la suivante sans ressaisie inutile.
          </Typography>
        </Box>
      </Paper>
    </Paper>
  )
}

function BenefitCard({ item }) {
  return (
    <Paper
      elevation={0}
      sx={{
        height: "100%",
        p: { xs: 2.2, md: 2.6 },
        borderRadius: 3.5,
        bgcolor: "#ffffff",
        border: "1px solid rgba(148,163,184,0.22)",
        boxShadow: "0 12px 34px rgba(15,23,42,0.05)",
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          display: "grid",
          placeItems: "center",
          borderRadius: 2.6,
          color: "#1d4ed8",
          bgcolor: "#eff6ff",
        }}
      >
        {item.icon}
      </Box>
      <Typography sx={{ mt: 1.5, color: "#0f172a", fontWeight: 950, fontSize: "1.08rem" }}>
        {item.title}
      </Typography>
      <Typography sx={{ mt: 0.8, color: "#64748b", lineHeight: 1.7, fontSize: "0.9rem" }}>
        {item.text}
      </Typography>
    </Paper>
  )
}

// =========================================================
// PAGE
// =========================================================

function LandingPage() {
  const theme = useTheme()
  const isNavCompact = useMediaQuery(theme.breakpoints.down("lg"))
  const [openMenu, setOpenMenu] = useState(false)

  const navItems = useMemo(
    () => [
      { label: "Processus", href: "#modules" },
      { label: "Automatisation", href: "#automation" },
      { label: "POS & terrain", href: "#pos" },
      { label: "Secteurs", href: "#secteurs" },
      { label: "Tarifs", href: "#tarifs" },
      { label: "À propos", href: "#about" },
      { label: "Contact", href: "#contact" },
    ],
    []
  )

  const closeMenu = () => setOpenMenu(false)

  return (
    <Box
      id="top"
      sx={{
        minHeight: "100vh",
        overflowX: "hidden",
        color: "#0f172a",
        bgcolor: "#ffffff",
      }}
    >
      {/* =====================================================
          HEADER
      ====================================================== */}
      <Box
        component="header"
        sx={{
          position: "fixed",
          inset: "0 0 auto 0",
          zIndex: 1200,
          bgcolor: "rgba(255,255,255,0.94)",
          backdropFilter: "blur(18px)",
          WebkitBackdropFilter: "blur(18px)",
          borderBottom: "1px solid rgba(148,163,184,0.18)",
          boxShadow: "0 8px 28px rgba(15,23,42,0.055)",
        }}
      >
        <Container maxWidth="xl" sx={{ px: { xs: 1.5, sm: 2.5, md: 3.5, xl: 4 } }}>
          <Box
            sx={{
              minHeight: { xs: 78, md: 88 },
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Box
              component="a"
              href="#top"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.1,
                minWidth: 0,
                color: "inherit",
                textDecoration: "none",
              }}
            >
              <Box
                sx={{
                  width: { xs: 48, md: 54 },
                  height: { xs: 48, md: 54 },
                  flexShrink: 0,
                  p: 0.45,
                  borderRadius: 2.5,
                  bgcolor: "#ffffff",
                  border: "1px solid rgba(148,163,184,0.20)",
                  boxShadow: "0 8px 22px rgba(15,23,42,0.09)",
                }}
              >
                <Box
                  component="img"
                  src="/rlsys.png"
                  alt="RLSys Business"
                  sx={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={0.8} sx={{ minWidth: 0 }}>
                  <Typography
                    noWrap
                    sx={{
                      color: "#0f172a",
                      fontSize: { xs: "1.04rem", sm: "1.18rem", md: "1.28rem" },
                      fontWeight: 950,
                      letterSpacing: "-0.025em",
                    }}
                  >
                    RLSys Business
                  </Typography>
                  <Chip
                    label="ERP"
                    size="small"
                    sx={{
                      height: 32,
                      flexShrink: 0,
                      color: "#ffffff",
                      bgcolor: "#2563eb",
                      fontWeight: 950,
                    }}
                  />
                </Stack>
                <Typography
                  noWrap
                  sx={{
                    mt: 0.15,
                    color: "#64748b",
                    fontSize: { xs: "0.68rem", md: "0.73rem" },
                    fontWeight: 750,
                  }}
                >
                  Automatisation des opérations • All-in-One
                </Typography>
              </Box>
            </Box>

            {isNavCompact ? (
              <IconButton
                aria-label="Ouvrir le menu"
                onClick={() => setOpenMenu(true)}
                sx={{
                  ml: "auto",
                  width: 46,
                  height: 46,
                  borderRadius: 2.5,
                  color: "#0f172a",
                  bgcolor: "#f8fafc",
                  border: "1px solid #e2e8f0",
                }}
              >
                <MenuRoundedIcon />
              </IconButton>
            ) : (
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.35}
                sx={{
                  ml: "auto",
                  minWidth: 0,
                }}
              >
                {navItems.map((item) => (
                  <NavButton key={item.href} href={item.href}>
                    {item.label}
                  </NavButton>
                ))}

                <DownloadBrochureButton />

                <Button
                  component={RouterLink}
                  to="/login"
                  variant="outlined"
                  sx={{
                    ml: 0.35,
                    minHeight: 46,
                    px: 2.25,
                    fontSize: "0.9rem",
                    borderRadius: 999,
                    fontWeight: 900,
                    textTransform: "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  Se connecter
                </Button>

                <Button
                  component="a"
                  href={whatsappUrl}
                  target="_blank"
                  rel="noreferrer"
                  variant="contained"
                  startIcon={<WhatsAppIcon />}
                  sx={{
                    minHeight: 46,
                    px: 2.25,
                    borderRadius: 999,
                    fontSize: "0.9rem",
                    fontWeight: 900,
                    textTransform: "none",
                    whiteSpace: "nowrap",
                    boxShadow: "0 10px 24px rgba(37,99,235,0.22)",
                  }}
                >
                  Démo
                </Button>
              </Stack>
            )}
          </Box>
        </Container>
      </Box>

      {/* =====================================================
          DRAWER
      ====================================================== */}
      <Drawer
        anchor="right"
        open={openMenu}
        onClose={closeMenu}
        PaperProps={{
          sx: {
            width: { xs: "88vw", sm: 365 },
            maxWidth: 365,
            borderRadius: "28px 0 0 28px",
          },
        }}
      >
        <Box sx={{ p: { xs: 2.4, sm: 3 } }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                component="img"
                src="/rlsys.png"
                alt="RLSys Business"
                sx={{ width: 44, height: 44, objectFit: "contain" }}
              />
              <Box>
                <Typography fontWeight={950}>RLSys Business</Typography>
                <Typography sx={{ color: "#64748b", fontSize: "0.68rem" }}>
                  ERP All-in-One
                </Typography>
              </Box>
            </Stack>

            <IconButton onClick={closeMenu} aria-label="Fermer le menu">
              <CloseRoundedIcon />
            </IconButton>
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Stack spacing={0.6}>
            {navItems.map((item) => (
              <Button
                key={item.href}
                component="a"
                href={item.href}
                onClick={closeMenu}
                fullWidth
                sx={{
                  minHeight: 48,
                  justifyContent: "flex-start",
                  px: 1.5,
                  color: "#1e293b",
                  borderRadius: 2.5,
                  fontWeight: 850,
                  textTransform: "none",
                }}
              >
                {item.label}
              </Button>
            ))}
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Stack spacing={1.1}>
            <DownloadBrochureButton fullWidth />
            <Button
              component={RouterLink}
              to="/login"
              onClick={closeMenu}
              fullWidth
              variant="outlined"
              sx={{ minHeight: 50, borderRadius: 2.8, fontWeight: 900, textTransform: "none" }}
            >
              Se connecter
            </Button>
            <Button
              component="a"
              href={whatsappUrl}
              target="_blank"
              rel="noreferrer"
              fullWidth
              variant="contained"
              startIcon={<WhatsAppIcon />}
              sx={{ minHeight: 52, borderRadius: 2.8, fontWeight: 900, textTransform: "none" }}
            >
              Demander une démonstration
            </Button>
          </Stack>
        </Box>
      </Drawer>

      {/* =====================================================
          HERO
      ====================================================== */}
      <Box
        component="section"
        sx={{
          position: "relative",
          overflow: "hidden",
          isolation: "isolate",
          pt: { xs: 13, sm: 14, md: 15 },
          pb: { xs: 6, sm: 7, lg: 9 },
          backgroundColor: "#f8fbff",
          backgroundImage: `
            radial-gradient(circle at 12% 18%, rgba(37,99,235,0.10) 0%, rgba(37,99,235,0) 25%),
            radial-gradient(circle at 88% 18%, rgba(79,70,229,0.16) 0%, rgba(79,70,229,0) 30%),
            radial-gradient(circle at 76% 74%, rgba(14,165,233,0.11) 0%, rgba(14,165,233,0) 30%),
            linear-gradient(145deg,#ffffff 0%,#f8fbff 44%,#edf4ff 100%)
          `,
          borderBottom: "1px solid rgba(148,163,184,0.14)",

          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            zIndex: -2,
            opacity: 0.42,
            backgroundImage: `
              linear-gradient(rgba(30,64,175,0.045) 1px, transparent 1px),
              linear-gradient(90deg, rgba(30,64,175,0.045) 1px, transparent 1px)
            `,
            backgroundSize: "44px 44px",
            maskImage:
              "linear-gradient(to bottom, rgba(0,0,0,0.85), rgba(0,0,0,0.18) 68%, transparent 100%)",
            WebkitMaskImage:
              "linear-gradient(to bottom, rgba(0,0,0,0.85), rgba(0,0,0,0.18) 68%, transparent 100%)",
          },

          "&::after": {
            content: '""',
            position: "absolute",
            width: { xs: 360, md: 620 },
            height: { xs: 360, md: 620 },
            right: { xs: -220, md: -240 },
            top: { xs: 130, md: 90 },
            zIndex: -1,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(37,99,235,0.16) 0%, rgba(79,70,229,0.08) 42%, rgba(255,255,255,0) 72%)",
            filter: "blur(2px)",
            pointerEvents: "none",
          },
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            position: "relative",
            zIndex: 2,
            px: { xs: 2, sm: 3 },

            "&::before": {
              content: '""',
              position: "absolute",
              left: { xs: -90, md: -130 },
              bottom: { xs: 20, md: -10 },
              width: { xs: 220, md: 330 },
              height: { xs: 220, md: 330 },
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(37,99,235,0.08), rgba(37,99,235,0) 70%)",
              pointerEvents: "none",
            },
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "0.96fr 1.04fr" },
              gap: { xs: 4, md: 5, lg: 6 },
              alignItems: "center",
            }}
          >
            <Box>
              <SectionLabel>
                ERP All-in-One • Automatisation de bout en bout
              </SectionLabel>

              <Typography
                component="h1"
                sx={{
                  mt: 2,
                  maxWidth: 820,
                  color: "#08142d",
                  fontSize: { xs: "2.55rem", sm: "3.4rem", md: "4rem", lg: "4.35rem" },
                  lineHeight: { xs: 1.03, md: 0.99 },
                  letterSpacing: "-0.055em",
                  fontWeight: 950,
                }}
              >
                Automatisez vos opérations. Pilotez toute votre entreprise depuis une seule plateforme.
              </Typography>

              <Typography
                sx={{
                  mt: 2.3,
                  maxWidth: 790,
                  color: "#475569",
                  fontSize: { xs: "1rem", sm: "1.07rem", md: "1.11rem" },
                  lineHeight: 1.75,
                }}
              >
                RLSys Business connecte l’ensemble du cycle opérationnel : fournisseurs & achats,
                production, entrepôts, stock & inventaires, ventes, POS, commerciaux terrain,
                paiements, caisse, finance, logistique, RH et reporting. Vos équipes travaillent
                dans un même système, avec des workflows structurés et des données à jour.
              </Typography>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.1}
                sx={{ mt: 3.2, "& > *": { width: { xs: "100%", sm: "auto" } } }}
              >
                <Button
                  component="a"
                  href={whatsappUrl}
                  target="_blank"
                  rel="noreferrer"
                  variant="contained"
                  size="large"
                  startIcon={<WhatsAppIcon />}
                  sx={{
                    minHeight: 56,
                    px: 3,
                    borderRadius: 3,
                    fontWeight: 950,
                    textTransform: "none",
                    boxShadow: "0 14px 30px rgba(37,99,235,0.24)",
                  }}
                >
                  Demander une démonstration
                </Button>

                <DownloadBrochureButton variant="outlined" size="large" />

                <Button
                  component={RouterLink}
                  to="/login"
                  variant="text"
                  size="large"
                  sx={{ minHeight: 56, px: 2.4, borderRadius: 3, fontWeight: 900, textTransform: "none" }}
                >
                  Accéder à RLSys
                </Button>
              </Stack>

              <Box
                sx={{
                  mt: 3,
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(3,minmax(0,1fr))" },
                  gap: 1,
                  maxWidth: 760,
                }}
              >
                {[
                  ["Processus connectés", "Achats → Production → Vente"],
                  ["Multi-sites", "Entrepôts • Shops • Terrain"],
                  ["Pilotage", "KPI • Contrôle • Traçabilité"],
                ].map(([title, text]) => (
                  <Paper
                    key={title}
                    elevation={0}
                    sx={{
                      p: 1.2,
                      borderRadius: 2.5,
                      bgcolor: "rgba(255,255,255,0.78)",
                      border: "1px solid rgba(148,163,184,0.22)",
                    }}
                  >
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <CheckCircleRoundedIcon sx={{ color: "#16a34a", fontSize: 19 }} />
                      <Typography sx={{ color: "#0f172a", fontSize: "0.8rem", fontWeight: 900 }}>
                        {title}
                      </Typography>
                    </Stack>
                    <Typography sx={{ mt: 0.45, color: "#64748b", fontSize: "0.7rem", lineHeight: 1.4 }}>
                      {text}
                    </Typography>
                  </Paper>
                ))}
              </Box>
            </Box>

            <OperationsPreview />
          </Box>
        </Container>
      </Box>

      {/* =====================================================
          PROCESSES / MODULES
      ====================================================== */}
      <Box component="section" id="modules" sx={{ ...sectionSx, py: { xs: 6, md: 9 }, bgcolor: "#ffffff" }}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 } }}>
          <Stack spacing={1.2} alignItems="center" textAlign="center" sx={{ mb: { xs: 4, md: 5 } }}>
            <SectionLabel>Processus couverts par RLSys Business</SectionLabel>
            <Typography
              component="h2"
              sx={{
                maxWidth: 980,
                color: "#0f172a",
                fontSize: { xs: "2rem", sm: "2.55rem", md: "3.15rem" },
                lineHeight: 1.07,
                letterSpacing: "-0.04em",
                fontWeight: 950,
              }}
            >
              De l’achat des matières jusqu’au reporting de direction.
            </Typography>
            <Typography sx={{ maxWidth: 900, color: "#64748b", lineHeight: 1.8 }}>
              RLSys Business ne se limite pas à la vente et au stock. La plateforme couvre les opérations
              centrales de l’entreprise et relie les départements pour réduire les doubles saisies,
              fluidifier les validations et améliorer le contrôle.
            </Typography>
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2,minmax(0,1fr))", lg: "repeat(4,minmax(0,1fr))" },
              gap: { xs: 1.4, md: 1.7 },
            }}
          >
            {modules.map((item) => (
              <ModuleCard key={item.title} item={item} />
            ))}
          </Box>
        </Container>
      </Box>

      {/* =====================================================
          AUTOMATION
      ====================================================== */}
      <Box
        component="section"
        id="automation"
        sx={{
          ...sectionSx,
          py: { xs: 6, md: 9 },
          background: "linear-gradient(180deg,#f8fbff 0%,#eef4ff 100%)",
          borderTop: "1px solid rgba(148,163,184,0.12)",
          borderBottom: "1px solid rgba(148,163,184,0.12)",
        }}
      >
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 } }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "0.9fr 1.1fr" },
              gap: { xs: 3.5, lg: 5 },
              alignItems: "center",
            }}
          >
            <Box>
              <SectionLabel>Automatisation All-in-One</SectionLabel>
              <Typography
                component="h2"
                sx={{
                  mt: 1.6,
                  color: "#0f172a",
                  maxWidth: 720,
                  fontSize: { xs: "2rem", sm: "2.55rem", md: "3.1rem" },
                  lineHeight: 1.08,
                  letterSpacing: "-0.04em",
                  fontWeight: 950,
                }}
              >
                Un processus terminé devient l’entrée du processus suivant.
              </Typography>
              <Typography sx={{ mt: 1.8, maxWidth: 760, color: "#64748b", lineHeight: 1.8 }}>
                L’objectif est de réduire les opérations manuelles inutiles. Une réception peut mettre à jour le stock,
                un ordre de production peut consommer des matières et créer des produits finis, une vente peut alimenter
                la caisse et les dettes, et une livraison peut clôturer automatiquement le cycle opérationnel.
              </Typography>

              <Stack spacing={1.1} sx={{ mt: 2.5 }}>
                {[
                  "Validations et changements de statut structurés",
                  "Alertes stock, contrôles et rapprochements",
                  "Données partagées entre services sans ressaisie inutile",
                  "Historique complet pour comprendre qui a fait quoi et quand",
                ].map((item) => (
                  <Stack key={item} direction="row" spacing={1} alignItems="flex-start">
                    <CheckCircleRoundedIcon sx={{ mt: "2px", color: "#16a34a", fontSize: 21 }} />
                    <Typography sx={{ color: "#475569", lineHeight: 1.6, fontWeight: 750 }}>{item}</Typography>
                  </Stack>
                ))}
              </Stack>

              <Button
                component="a"
                href={whatsappUrl}
                target="_blank"
                rel="noreferrer"
                variant="contained"
                startIcon={<WhatsAppIcon />}
                sx={{ mt: 2.8, minHeight: 52, px: 2.7, borderRadius: 3, fontWeight: 900, textTransform: "none" }}
              >
                Présenter mon workflow
              </Button>
            </Box>

            <Paper
              elevation={0}
              sx={{
                p: { xs: 1.3, sm: 1.6 },
                borderRadius: 4,
                bgcolor: "#ffffff",
                border: "1px solid rgba(37,99,235,0.12)",
                boxShadow: "0 20px 55px rgba(15,23,42,0.08)",
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 0.3, mb: 1.2 }}>
                <Box>
                  <Typography sx={{ color: "#0f172a", fontWeight: 950 }}>Chaîne opérationnelle</Typography>
                  <Typography variant="caption" sx={{ color: "#64748b" }}>
                    Exemple d’un cycle connecté dans RLSys
                  </Typography>
                </Box>
                <Chip label="Automatisé" size="small" color="primary" sx={{ fontWeight: 900 }} />
              </Stack>

              <Stack spacing={0.8}>
                {processFlows.map((item) => (
                  <ProcessRow key={item.number} item={item} />
                ))}
              </Stack>
            </Paper>
          </Box>
        </Container>
      </Box>

      {/* =====================================================
          BENEFITS
      ====================================================== */}
      <Box component="section" sx={{ py: { xs: 6, md: 8 }, bgcolor: "#ffffff" }}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 } }}>
          <Stack spacing={1.1} alignItems="center" textAlign="center" sx={{ mb: 4 }}>
            <SectionLabel>Pourquoi une plateforme unique ?</SectionLabel>
            <Typography
              component="h2"
              sx={{
                maxWidth: 900,
                color: "#0f172a",
                fontSize: { xs: "2rem", sm: "2.45rem", md: "2.9rem" },
                lineHeight: 1.08,
                fontWeight: 950,
                letterSpacing: "-0.035em",
              }}
            >
              Moins de dispersion. Plus de contrôle. Plus de visibilité.
            </Typography>
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2,minmax(0,1fr))", lg: "repeat(4,minmax(0,1fr))" },
              gap: 1.6,
            }}
          >
            {benefits.map((item) => (
              <BenefitCard key={item.title} item={item} />
            ))}
          </Box>
        </Container>
      </Box>

      {/* =====================================================
          POS / FIELD
      ====================================================== */}
      <Box component="section" id="pos" sx={{ ...sectionSx, py: { xs: 6, md: 8.5 }, bgcolor: "#ffffff" }}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 } }}>
          <Paper
            elevation={0}
            sx={{
              position: "relative",
              overflow: "hidden",
              p: { xs: 2.5, sm: 3.5, md: 5 },
              borderRadius: { xs: 4, md: 5 },
              color: "#ffffff",
              background: "linear-gradient(135deg,#07142f 0%,#143a9a 58%,#2563eb 100%)",
              boxShadow: "0 28px 70px rgba(30,64,175,0.18)",
            }}
          >
            <Box
              sx={{
                position: "relative",
                zIndex: 2,
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1.05fr 0.95fr" },
                gap: { xs: 3, md: 5 },
                alignItems: "center",
              }}
            >
              <Box>
                <SectionLabel dark>Web • Mobile • Tablette • POS • Terrain</SectionLabel>
                <Typography
                  component="h2"
                  sx={{
                    mt: 1.8,
                    maxWidth: 760,
                    fontSize: { xs: "2rem", sm: "2.55rem", md: "3.15rem" },
                    lineHeight: 1.07,
                    letterSpacing: "-0.04em",
                    fontWeight: 950,
                  }}
                >
                  Le système suit l’entreprise, du bureau jusqu’au terrain.
                </Typography>
                <Typography sx={{ mt: 1.8, maxWidth: 760, color: "rgba(255,255,255,0.78)", lineHeight: 1.8 }}>
                  Direction, entrepôts, boutiques, caissiers, agents commerciaux, chauffeurs et équipes opérationnelles
                  peuvent disposer d’interfaces adaptées à leurs rôles et à leurs appareils.
                </Typography>

                <Box
                  sx={{
                    mt: 2.6,
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "repeat(2,minmax(0,1fr))" },
                    gap: 1,
                  }}
                >
                  {[
                    "Vente rapide & tickets",
                    "Agents commerciaux terrain",
                    "Stocks par site en temps réel",
                    "Paiements & encaissements",
                    "Livraison & réception",
                    "Mode hors-ligne selon configuration",
                  ].map((item) => (
                    <Stack key={item} direction="row" spacing={0.9} alignItems="flex-start">
                      <CheckCircleRoundedIcon sx={{ mt: "2px", color: "#86efac", fontSize: 20 }} />
                      <Typography sx={{ color: "rgba(255,255,255,0.92)", fontSize: "0.92rem", fontWeight: 750 }}>
                        {item}
                      </Typography>
                    </Stack>
                  ))}
                </Box>
              </Box>

              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2.2, md: 2.8 },
                  borderRadius: 4,
                  color: "#0f172a",
                  bgcolor: "rgba(255,255,255,0.97)",
                  border: "1px solid rgba(255,255,255,0.35)",
                }}
              >
                <Stack direction="row" spacing={1.2} alignItems="center">
                  <Box
                    sx={{
                      width: 58,
                      height: 58,
                      display: "grid",
                      placeItems: "center",
                      borderRadius: 3,
                      color: "#ffffff",
                      background: "linear-gradient(135deg,#2563eb,#4f46e5)",
                    }}
                  >
                    <PhoneIphoneRoundedIcon sx={{ fontSize: 32 }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 950, fontSize: "1.25rem" }}>Expérience multi-appareils</Typography>
                    <Typography variant="body2" sx={{ mt: 0.3, color: "#64748b" }}>
                      Responsive et adaptée aux usages opérationnels.
                    </Typography>
                  </Box>
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: 1 }}>
                  {[
                    ["Direction", <DashboardRoundedIcon />],
                    ["POS", <PointOfSaleRoundedIcon />],
                    ["Terrain", <StorefrontRoundedIcon />],
                    ["Logistique", <LocalShippingRoundedIcon />],
                  ].map(([label, icon]) => (
                    <Paper
                      key={label}
                      variant="outlined"
                      sx={{ p: 1.3, borderRadius: 2.6, bgcolor: "#f8fafc" }}
                    >
                      <Box sx={{ color: "#2563eb" }}>{icon}</Box>
                      <Typography sx={{ mt: 0.5, fontSize: "0.8rem", fontWeight: 900 }}>{label}</Typography>
                    </Paper>
                  ))}
                </Box>
              </Paper>
            </Box>
          </Paper>
        </Container>
      </Box>

      {/* =====================================================
          SECTORS
      ====================================================== */}
      <Box
        component="section"
        id="secteurs"
        sx={{ ...sectionSx, py: { xs: 6, md: 8.5 }, background: "linear-gradient(180deg,#ffffff,#f8fafc)" }}
      >
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 } }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "0.72fr 1.28fr" },
              gap: { xs: 3.5, lg: 5 },
              alignItems: "center",
            }}
          >
            <Box>
              <SectionLabel>Secteurs & organisations</SectionLabel>
              <Typography
                component="h2"
                sx={{
                  mt: 1.7,
                  maxWidth: 680,
                  color: "#0f172a",
                  fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                  lineHeight: 1.08,
                  letterSpacing: "-0.04em",
                  fontWeight: 950,
                }}
              >
                Un socle ERP adaptable à votre réalité métier.
              </Typography>
              <Typography sx={{ mt: 1.8, maxWidth: 680, color: "#64748b", lineHeight: 1.8 }}>
                Le même socle peut être configuré pour une petite structure, un distributeur, une usine,
                un réseau de points de vente ou une entreprise multi-sites avec équipes terrain.
              </Typography>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "repeat(2,minmax(0,1fr))", sm: "repeat(3,minmax(0,1fr))", md: "repeat(4,minmax(0,1fr))" },
                gap: 1,
              }}
            >
              {sectors.map((item) => (
                <Paper
                  key={item}
                  elevation={0}
                  sx={{
                    minHeight: 78,
                    p: 1.4,
                    display: "grid",
                    placeItems: "center",
                    borderRadius: 2.8,
                    textAlign: "center",
                    color: "#334155",
                    bgcolor: "#ffffff",
                    border: "1px solid rgba(148,163,184,0.22)",
                    boxShadow: "0 8px 22px rgba(15,23,42,0.035)",
                    fontSize: "0.82rem",
                    fontWeight: 850,
                  }}
                >
                  {item}
                </Paper>
              ))}
            </Box>
          </Box>
        </Container>
      </Box>

      {/* =====================================================
          BROCHURE
      ====================================================== */}
      <Box component="section" sx={{ py: { xs: 5.5, md: 7.5 }, bgcolor: "#ffffff" }}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 } }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2.5, sm: 3.5, md: 4.5 },
              borderRadius: { xs: 4, md: 5 },
              background: "linear-gradient(135deg,#eff6ff 0%,#ffffff 50%,#eef2ff 100%)",
              border: "1px solid rgba(37,99,235,0.12)",
              boxShadow: "0 20px 55px rgba(15,23,42,0.07)",
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "130px minmax(0,1fr) auto" },
                gap: { xs: 2, md: 3 },
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  width: { xs: 72, md: 100 },
                  height: { xs: 72, md: 100 },
                  display: "grid",
                  placeItems: "center",
                  borderRadius: 3.5,
                  color: "#ffffff",
                  background: "linear-gradient(135deg,#1d4ed8,#4f46e5)",
                  boxShadow: "0 16px 36px rgba(37,99,235,0.23)",
                }}
              >
                <PictureAsPdfRoundedIcon sx={{ fontSize: { xs: 34, md: 48 } }} />
              </Box>

              <Box>
                <Typography sx={{ color: "#1d4ed8", fontWeight: 900, fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                  Brochure commerciale
                </Typography>
                <Typography
                  sx={{ mt: 0.55, color: "#0f172a", fontSize: { xs: "1.6rem", md: "2rem" }, fontWeight: 950, lineHeight: 1.15 }}
                >
                  Découvrez l’écosystème RLSys Business en détail.
                </Typography>
                <Typography sx={{ mt: 0.8, maxWidth: 900, color: "#64748b", lineHeight: 1.7 }}>
                  Production, fournisseurs & achats, entrepôts, stock & inventaire, ventes, POS, terrain,
                  paiements, finance, logistique, RH, reporting, sécurité et déploiement.
                </Typography>
              </Box>

              <DownloadBrochureButton variant="contained" size="large" />
            </Box>
          </Paper>
        </Container>
      </Box>

      {/* =====================================================
          PRICING
      ====================================================== */}
      <Box
        component="section"
        id="tarifs"
        sx={{ ...sectionSx, py: { xs: 6, md: 9 }, background: "linear-gradient(180deg,#f8fafc 0%,#eff6ff 55%,#ffffff 100%)" }}
      >
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 } }}>
          <Stack spacing={1.2} alignItems="center" textAlign="center" sx={{ mb: 4.5 }}>
            <SectionLabel>Tarification & accompagnement</SectionLabel>
            <Typography
              component="h2"
              sx={{
                maxWidth: 920,
                color: "#0f172a",
                fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                lineHeight: 1.08,
                letterSpacing: "-0.04em",
                fontWeight: 950,
              }}
            >
              Un investissement adapté à votre périmètre, pas un prix figé.
            </Typography>
            <Typography sx={{ maxWidth: 900, color: "#64748b", lineHeight: 1.8 }}>
              Chaque entreprise n’automatise pas les mêmes processus. Nous évaluons d’abord les modules,
              les sites, les utilisateurs, les intégrations et le niveau de personnalisation avant de proposer
              une estimation claire et adaptée à votre organisation.
            </Typography>

            <Paper
              elevation={0}
              sx={{
                mt: 1.2,
                px: 2,
                py: 1.35,
                maxWidth: 880,
                borderRadius: 3,
                bgcolor: "rgba(255,255,255,0.74)",
                border: "1px solid rgba(37,99,235,0.12)",
              }}
            >
              <Typography
                sx={{
                  color: "#334155",
                  fontSize: "0.88rem",
                  lineHeight: 1.65,
                  fontWeight: 700,
                }}
              >
                Notre approche : comprendre votre fonctionnement, définir le périmètre utile,
                puis chiffrer le projet. Vous évitez ainsi de payer pour des modules ou des
                fonctionnalités dont vous n’avez pas besoin.
              </Typography>
            </Paper>
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2,minmax(0,1fr))", lg: "repeat(4,minmax(0,1fr))" },
              gap: 1.7,
              alignItems: "stretch",
            }}
          >
            {pricingPlans.map((plan) => (
              <Paper
                key={plan.title}
                elevation={0}
                sx={{
                  position: "relative",
                  height: "100%",
                  p: 2.5,
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: 3.8,
                  bgcolor: "#ffffff",
                  border: plan.featured ? "2px solid #2563eb" : "1px solid rgba(148,163,184,0.24)",
                  boxShadow: plan.featured
                    ? "0 24px 58px rgba(37,99,235,0.15)"
                    : "0 14px 38px rgba(15,23,42,0.06)",
                }}
              >
                {plan.featured ? (
                  <Chip label="Recommandé PME" color="primary" size="small" sx={{ alignSelf: "flex-start", mb: 1.3, fontWeight: 900 }} />
                ) : null}

                <Typography sx={{ color: "#0f172a", fontSize: "1.28rem", fontWeight: 950, lineHeight: 1.25 }}>
                  {plan.title}
                </Typography>
                <Typography sx={{ mt: 1, color: "#64748b", lineHeight: 1.65, fontSize: "0.9rem" }}>
                  {plan.description}
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Typography sx={{ color: "#64748b", fontSize: "0.7rem", fontWeight: 900, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                  Tarification du projet
                </Typography>
                <Typography sx={{ mt: 0.45, color: "#1d4ed8", fontSize: "1.75rem", fontWeight: 950, lineHeight: 1.15 }}>
                  {plan.price}
                </Typography>

                <Stack spacing={0.95} sx={{ mt: 2, mb: 2.3, flexGrow: 1 }}>
                  {plan.features.map((feature) => (
                    <Stack key={feature} direction="row" spacing={0.8} alignItems="flex-start">
                      <CheckCircleRoundedIcon sx={{ mt: "2px", color: "#16a34a", fontSize: 18 }} />
                      <Typography variant="body2" sx={{ color: "#475569", lineHeight: 1.5 }}>
                        {feature}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>

                <Button
                  component="a"
                  href={whatsappUrl}
                  target="_blank"
                  rel="noreferrer"
                  variant={plan.featured ? "contained" : "outlined"}
                  startIcon={<WhatsAppIcon />}
                  fullWidth
                  sx={{ minHeight: 48, borderRadius: 2.8, fontWeight: 900, textTransform: "none" }}
                >
                  {plan.price === "Sur devis" ? "Demander un devis" : "Obtenir une estimation"}
                </Button>
              </Paper>
            ))}
          </Box>

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={{ xs: 1.2, md: 2.2 }}
            alignItems={{ xs: "flex-start", md: "center" }}
            justifyContent="center"
            sx={{ mt: 3.5 }}
          >
            {[
              "Périmètre défini avant chiffrage",
              "Modules activés selon vos besoins",
              "Déploiement et support cadrés avec vous",
            ].map((item) => (
              <Stack
                key={item}
                direction="row"
                spacing={0.8}
                alignItems="center"
              >
                <CheckCircleRoundedIcon
                  sx={{ color: "#16a34a", fontSize: 18 }}
                />
                <Typography
                  variant="body2"
                  sx={{ color: "#64748b", fontWeight: 750 }}
                >
                  {item}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* =====================================================
          ABOUT
      ====================================================== */}
      <Box
        component="section"
        id="about"
        sx={{ ...sectionSx, py: { xs: 6, md: 8.5 }, background: "linear-gradient(180deg,#ffffff 0%,#f8fbff 100%)" }}
      >
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 } }}>
          <Stack spacing={1.2} alignItems="center" textAlign="center" sx={{ mb: 4.5 }}>
            <SectionLabel>À propos de RLSys Business</SectionLabel>
            <Typography
              component="h2"
              sx={{
                maxWidth: 980,
                color: "#0f172a",
                fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                lineHeight: 1.08,
                letterSpacing: "-0.04em",
                fontWeight: 950,
              }}
            >
              Une plateforme conçue autour des processus réels de l’entreprise.
            </Typography>
            <Typography sx={{ maxWidth: 930, color: "#64748b", lineHeight: 1.8 }}>
              RLSys Business vise à centraliser et automatiser les opérations quotidiennes dans une architecture cohérente,
              responsive et évolutive. Le système peut être configuré selon l’organisation, les rôles, les circuits de validation,
              les sites et les règles métier propres à chaque client.
            </Typography>
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "1.15fr 0.85fr" },
              gap: 2,
            }}
          >
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, md: 3.4 },
                borderRadius: 4,
                bgcolor: "#ffffff",
                border: "1px solid rgba(148,163,184,0.22)",
                boxShadow: "0 16px 42px rgba(15,23,42,0.06)",
              }}
            >
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "flex-start", sm: "center" }}>
                <Box
                  sx={{
                    width: 72,
                    height: 72,
                    flexShrink: 0,
                    display: "grid",
                    placeItems: "center",
                    borderRadius: 3.3,
                    color: "#ffffff",
                    background: "linear-gradient(135deg,#1d4ed8,#4338ca)",
                  }}
                >
                  <PersonRoundedIcon sx={{ fontSize: 38 }} />
                </Box>
                <Box>
                  <Typography sx={{ color: "#2563eb", fontSize: "0.72rem", fontWeight: 950, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                    Fondateur & concepteur
                  </Typography>
                  <Typography sx={{ mt: 0.3, color: "#0f172a", fontSize: "1.7rem", fontWeight: 950 }}>
                    Ruben Kanku
                  </Typography>
                  <Typography sx={{ color: "#64748b", fontWeight: 750 }}>
                    Data • Automatisation • Développement de solutions numériques
                  </Typography>
                </Box>
              </Stack>

              <Typography sx={{ mt: 2.1, color: "#475569", lineHeight: 1.8 }}>
                RLSys Business est développé avec une approche orientée opérations, données et automatisation afin de fournir
                aux entreprises un outil pratique pour exécuter, contrôler et piloter leurs activités.
              </Typography>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, md: 3.4 },
                borderRadius: 4,
                color: "#ffffff",
                background: "linear-gradient(145deg,#07142f 0%,#172554 60%,#1e3a8a 100%)",
                boxShadow: "0 18px 48px rgba(15,23,42,0.14)",
              }}
            >
              <Box
                sx={{
                  width: 58,
                  height: 58,
                  display: "grid",
                  placeItems: "center",
                  borderRadius: 3,
                  color: "#1d4ed8",
                  bgcolor: "#ffffff",
                }}
              >
                <GroupsRoundedIcon sx={{ fontSize: 32 }} />
              </Box>
              <Typography sx={{ mt: 1.6, fontSize: "1.4rem", fontWeight: 950 }}>Collaboration & évolution</Typography>
              <Typography sx={{ mt: 0.9, color: "rgba(255,255,255,0.72)", lineHeight: 1.75 }}>
                L’écosystème évolue avec l’appui de collaborateurs et avec les besoins observés sur le terrain.
              </Typography>
              <Stack spacing={0.9} sx={{ mt: 1.8 }}>
                {["Joël Ndolo", "Hervé Kinkete"].map((name) => (
                  <Paper
                    key={name}
                    elevation={0}
                    sx={{
                      p: 1.25,
                      borderRadius: 2.6,
                      color: "#ffffff",
                      bgcolor: "rgba(255,255,255,0.07)",
                      border: "1px solid rgba(255,255,255,0.10)",
                    }}
                  >
                    <Typography sx={{ fontWeight: 900 }}>{name}</Typography>
                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.58)" }}>
                      Collaborateur RLSys Business
                    </Typography>
                  </Paper>
                ))}
              </Stack>
            </Paper>
          </Box>
        </Container>
      </Box>

      {/* =====================================================
          CONTACT
      ====================================================== */}
      <Box component="section" id="contact" sx={{ ...sectionSx, py: { xs: 6, md: 8 }, bgcolor: "#061024" }}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 } }}>
          <Paper
            elevation={0}
            sx={{
              position: "relative",
              overflow: "hidden",
              p: { xs: 2.6, sm: 3.5, md: 4.5 },
              borderRadius: { xs: 4, md: 5 },
              color: "#ffffff",
              background: "linear-gradient(135deg,#123da8 0%,#2855dc 50%,#4f46e5 100%)",
              boxShadow: "0 28px 75px rgba(0,0,0,0.22)",
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1.08fr 0.92fr" },
                gap: { xs: 3, md: 4 },
                alignItems: "center",
              }}
            >
              <Box>
                <SectionLabel dark>Démonstration RLSys Business</SectionLabel>
                <Typography
                  component="h2"
                  sx={{
                    mt: 1.6,
                    maxWidth: 760,
                    fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                    lineHeight: 1.08,
                    letterSpacing: "-0.04em",
                    fontWeight: 950,
                  }}
                >
                  Montrez-nous vos processus. Nous vous montrons comment les connecter.
                </Typography>
                <Typography sx={{ mt: 1.8, maxWidth: 760, color: "rgba(255,255,255,0.78)", lineHeight: 1.8 }}>
                  Une démonstration peut être orientée selon votre activité : achats, production, stock, ventes, POS,
                  terrain, caisse, finance, logistique, RH ou reporting.
                </Typography>

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.1}
                  sx={{ mt: 2.8, "& > *": { width: { xs: "100%", sm: "auto" } } }}
                >
                  <Button
                    component="a"
                    href={whatsappUrl}
                    target="_blank"
                    rel="noreferrer"
                    variant="contained"
                    color="success"
                    size="large"
                    startIcon={<WhatsAppIcon />}
                    sx={{ minHeight: 52, px: 3, borderRadius: 3, fontWeight: 950, textTransform: "none" }}
                  >
                    WhatsApp
                  </Button>
                  <DownloadBrochureButton variant="outlined" size="large" light />
                  <Button
                    component="a"
                    href={`mailto:${EMAIL}`}
                    variant="outlined"
                    size="large"
                    startIcon={<EmailRoundedIcon />}
                    sx={{
                      minHeight: 52,
                      px: 3,
                      borderRadius: 3,
                      color: "#ffffff",
                      borderColor: "rgba(255,255,255,0.62)",
                      fontWeight: 950,
                      textTransform: "none",
                    }}
                  >
                    Email
                  </Button>
                </Stack>
              </Box>

              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2.2, md: 2.7 },
                  borderRadius: 3.8,
                  color: "#ffffff",
                  bgcolor: "rgba(255,255,255,0.10)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255,255,255,0.16)",
                }}
              >
                <Typography sx={{ fontWeight: 950, fontSize: "1.1rem" }}>Contact RLSys Business</Typography>
                <Divider sx={{ my: 1.7, borderColor: "rgba(255,255,255,0.16)" }} />
                <Stack spacing={1.4}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <WhatsAppIcon />
                    <Box>
                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.58)" }}>WhatsApp</Typography>
                      <Typography sx={{ fontWeight: 850 }}>{WHATSAPP_DISPLAY}</Typography>
                    </Box>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <EmailRoundedIcon />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.58)" }}>Email</Typography>
                      <Typography sx={{ fontWeight: 850, overflowWrap: "anywhere" }}>{EMAIL}</Typography>
                    </Box>
                  </Stack>
                </Stack>
              </Paper>
            </Box>
          </Paper>
        </Container>
      </Box>

      {/* =====================================================
          FOOTER
      ====================================================== */}
      <Box
        component="footer"
        sx={{
          bgcolor: "#040b18",
          color: "#ffffff",
          borderTop: "1px solid rgba(255,255,255,0.07)",
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            px: { xs: 2, sm: 3, md: 3.5, xl: 4 },
            py: { xs: 4, md: 4.8 },
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "minmax(0, 1fr) auto",
              },
              gap: { xs: 3, md: 5 },
              alignItems: "center",
            }}
          >
            {/* BRAND */}
            <Stack
              direction="row"
              spacing={1.35}
              alignItems="center"
              sx={{ minWidth: 0 }}
            >
              <Box
                sx={{
                  width: 50,
                  height: 50,
                  flexShrink: 0,
                  p: 0.4,
                  borderRadius: 2.4,
                  bgcolor: "#ffffff",
                  boxShadow: "0 10px 26px rgba(0,0,0,0.18)",
                }}
              >
                <Box
                  component="img"
                  src="/rlsys.png"
                  alt="RLSys Business"
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontWeight: 950,
                    fontSize: "1.02rem",
                    lineHeight: 1.2,
                  }}
                >
                  RLSys Business ERP
                </Typography>

                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mt: 0.35,
                    color: "rgba(255,255,255,0.56)",
                    lineHeight: 1.45,
                  }}
                >
                  Automatisation All-in-One des processus de l’entreprise
                </Typography>
              </Box>
            </Stack>

            {/* NAVIGATION FOOTER */}
            <Box
              component="nav"
              aria-label="Navigation du pied de page"
              sx={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: { xs: "flex-start", md: "flex-end" },
                gap: { xs: 0.6, sm: 0.9 },
                maxWidth: { md: 720 },
              }}
            >
              {[
                ["Processus", "#modules"],
                ["Automatisation", "#automation"],
                ["POS & terrain", "#pos"],
                ["Secteurs", "#secteurs"],
                ["Tarifs", "#tarifs"],
                ["À propos", "#about"],
                ["Contact", "#contact"],
              ].map(([label, href]) => (
                <Typography
                  key={href}
                  component="a"
                  href={href}
                  variant="body2"
                  sx={{
                    minHeight: 38,
                    px: 1.25,
                    display: "inline-flex",
                    alignItems: "center",
                    borderRadius: 2,
                    color: "rgba(255,255,255,0.72)",
                    textDecoration: "none",
                    fontWeight: 800,
                    whiteSpace: "nowrap",
                    transition: "all .18s ease",
                    "&:hover": {
                      color: "#ffffff",
                      bgcolor: "rgba(255,255,255,0.07)",
                    },
                  }}
                >
                  {label}
                </Typography>
              ))}
            </Box>
          </Box>

          <Divider
            sx={{
              my: { xs: 2.8, md: 3.4 },
              borderColor: "rgba(255,255,255,0.10)",
            }}
          />

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={{ xs: 1, sm: 2.5 }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
          >
            <Typography
              variant="body2"
              sx={{
                color: "rgba(255,255,255,0.58)",
                lineHeight: 1.6,
              }}
            >
              © 2026 RLSys Business. Tous droits réservés.
            </Typography>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 0.45, sm: 2.2 }}
              alignItems={{ xs: "flex-start", sm: "center" }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: "rgba(255,255,255,0.48)",
                  fontWeight: 700,
                }}
              >
                ERP • Automatisation • Multi-sites • POS • Terrain
              </Typography>

              <Typography
                component="a"
                href={`mailto:${EMAIL}`}
                variant="caption"
                sx={{
                  color: "#93c5fd",
                  textDecoration: "none",
                  fontWeight: 800,
                  "&:hover": {
                    color: "#ffffff",
                  },
                }}
              >
                {EMAIL}
              </Typography>
            </Stack>
          </Stack>
        </Container>
      </Box>
    </Box>
  )
}

export default LandingPage
